/* eslint-disable max-len */
/**
 * TabBarIcon — the four bottom-nav tile icons. Each tile is a 51px circle
 * with a glyph centred inside. Two visual states (per the Figma):
 *   - inactive (default): subtle lavender bg, muted purple glyph
 *   - active (selected route): slightly more saturated bg + brighter glyph
 *
 * Paths/measurements are mirrored 1:1 from the Figma export so the icons
 * match design pixel-for-pixel.
 */
import React from 'react';

export type TabBarIconVariant = 'home' | 'shared' | 'reminders' | 'search';

type Props = {
  variant: TabBarIconVariant;
  active?: boolean;
  className?: string;
};

// Background-circle + glyph-stroke pairs for the two visual states.
const INACTIVE = { bg: '#EEEBF2', stroke: '#573A78' };
const ACTIVE = { bg: '#EEE3FB', stroke: '#895BBE' };

// Each variant returns SVG fragments — <path>, <circle>, etc. — that
// live inside the parent's 51×51 SVG. Coordinates come straight from
// the Figma export so positioning is pixel-exact.
const renderGlyph = (variant: TabBarIconVariant, color: string) => {
  switch (variant) {
    case 'home':
      return (
        <path
          d="M29.3569 28.8845V28.5149H22.6394V37.9932L22.6461 38H17.0773C15.5927 38 14.3903 36.7881 14.3903 35.2919V24.2632H11.6764C11.0517 24.2632 10.7628 23.4846 11.233 23.0716L25.1181 10.33C25.622 9.88998 26.3743 9.88998 26.8781 10.33L40.77 23.0716C41.2335 23.4778 40.9513 24.2564 40.3266 24.2564H37.6127V35.2851C37.6127 36.7814 36.4103 37.9932 34.9257 37.9932H29.3569L29.3569 33.5"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'search':
      return (
        <>
          <circle cx="24.1733" cy="23.246" r="11.1914" stroke={color} strokeWidth="1.5" />
          <path
            d="M34.1374 33.2101L38.9455 38.0182"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </>
      );
    case 'reminders':
      return (
        <>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.6094 20.8765C15.6058 18.2772 16.6329 15.784 18.4627 13.9505C20.2924 12.1169 22.7735 11.0948 25.3549 11.1109C30.6879 11.1508 34.9537 15.6142 34.9537 20.9991V21.9616C34.9537 26.8195 35.9631 29.6385 36.8521 31.1792C37.0471 31.5143 37.0493 31.9288 36.8579 32.2659C36.6665 32.6031 36.3108 32.8116 35.9251 32.8126H14.6369C14.2512 32.8116 13.8954 32.603 13.7041 32.2658C13.5127 31.9286 13.515 31.5141 13.71 31.179C14.5995 29.6382 15.6094 26.8193 15.6094 21.9616L15.6094 20.8765Z"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M20.9705 32.8127V34.028C20.9705 36.7128 23.0432 38.8893 25.6001 38.8893C28.157 38.8893 30.2298 36.7128 30.2298 34.028V32.8127"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'shared':
      return (
        <>
          <path
            d="M39.2241 18.7725C39.224 16.1769 37.1846 14.1151 34.7209 14.1148C32.2569 14.1148 30.2167 16.1768 30.2166 18.7725C30.2166 21.3683 32.2568 23.4302 34.7209 23.4302V24.9005L34.4137 24.893C31.266 24.7288 28.7628 22.0512 28.7628 18.7725C28.7629 15.3881 31.4307 12.6445 34.7209 12.6445L35.027 12.6519C38.1747 12.8161 40.6778 15.4938 40.6779 18.7725C40.6779 22.1568 38.0109 24.9002 34.7209 24.9005V23.4302C37.1847 23.4299 39.2241 21.3681 39.2241 18.7725Z"
            fill={color}
          />
          <path
            d="M34.7337 25.8397C39.8625 25.8397 43.9998 30.1132 44 35.3583C44 35.7644 43.6747 36.0945 43.2731 36.0945H35.1944C34.7929 36.0945 34.4676 35.7644 34.4676 35.3583C34.4678 34.9523 34.793 34.6231 35.1944 34.6231H42.5115C42.1505 30.5135 38.7969 27.31 34.7337 27.31C33.8467 27.31 32.9957 27.4627 32.2028 27.743C31.8237 27.8769 31.4085 27.6742 31.276 27.2908C31.144 26.9076 31.3442 26.4885 31.7231 26.3546C32.6677 26.0208 33.6807 25.8397 34.7337 25.8397Z"
            fill={color}
          />
          <path
            d="M25.4766 17.2234C25.4765 13.9709 22.9198 11.3764 19.8173 11.3763C16.7147 11.3763 14.1571 13.9708 14.157 17.2234C14.157 20.4762 16.7146 23.0705 19.8173 23.0705V24.5408L19.4512 24.5313C15.6924 24.3354 12.7032 21.1386 12.7032 17.2234C12.7033 13.1821 15.8885 9.90601 19.8173 9.90601L20.1834 9.91558C23.942 10.1117 26.9313 13.3085 26.9314 17.2234L26.9219 17.6C26.7315 21.4665 23.6234 24.5407 19.8173 24.5408V23.0705C22.9199 23.0704 25.4766 20.4761 25.4766 17.2234Z"
            fill={color}
          />
          <path
            d="M31.7747 36.4473C31.7747 31.1376 27.5185 26.833 22.2685 26.833H19.2674C13.2954 26.8332 8.45399 31.7297 8.45375 37.7697C8.45375 38.1372 8.74893 38.4357 9.11224 38.4357H28.7315C29.1331 38.4357 29.4584 38.7647 29.4584 39.1709C29.4584 39.577 29.1331 39.906 28.7315 39.906H9.11224C7.94582 39.906 7 38.9494 7 37.7697C7.00024 30.9175 12.4923 25.3629 19.2674 25.3627H22.2685C28.3216 25.3627 33.2284 30.3253 33.2284 36.4473C33.2284 36.8534 32.9031 37.1825 32.5016 37.1825C32.1 37.1825 31.7747 36.8534 31.7747 36.4473Z"
            fill={color}
          />
        </>
      );
    default:
      return null;
  }
};

export const TabBarIcon: React.FC<Props> = ({ variant, active, className }) => {
  const palette = active ? ACTIVE : INACTIVE;
  return (
    <div
      className={className || 'relative'}
      style={{ width: 51, height: 51 }}
      aria-hidden="true"
    >
      <svg
        width="51"
        height="51"
        viewBox="0 0 51 51"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ position: 'absolute', inset: 0 }}
      >
        <circle cx="25.5" cy="25.5" r="25.5" fill={palette.bg} />
        {renderGlyph(variant, palette.stroke)}
      </svg>
    </div>
  );
};

export default TabBarIcon;
